/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

// eslint-disable-next-line max-len
const selectQuietTimesCrudOperation = (state) => state.quietTimes.crudOperation;
const selectQuietTimesCrudStatus = (state) => state.quietTimes.crudStatus;
const selectQuietTimesInitialLoad = (state) => state.quietTimes.loading;
const selectQuietTimesError = (state) => state.quietTimes.error;
// eslint-disable-next-line max-len
const selectActiveQuietTime = (state) => state.quietTimes.activeQuietTime;
const selectQuietTimes = createSelector(
  (state) => state.quietTimes, (slice) => slice.quietTimeList,
);
const selectQuietTimeById = createSelector(
  [selectQuietTimes, (_, id) => id],
  (timeList, timeId) => timeList.find((time) => time.id === timeId),
);

const fetchQuietTime = createAsyncThunk(
  'securedApi/quietTimes/fetchSaved',
  async (id, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/quiet-time/${id}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const fetchQuietTimes = createAsyncThunk(
  'securedApi/quietTimes/fetchQuietTimes',
  async (searchType, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/quiet-time/`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const editQuietTime = createAsyncThunk(
  'securedApi/quietTimes/editQuietTime',
  async (quietTime, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .patch(`${envConfig.apiUrl}/api/v1/quiet-time/${quietTime.id}`,
          { data: { ...quietTime, patchFields: Object.keys(quietTime) } });
      return thunkAPI.fulfillWithValue({ ...quietTime });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const createQuietTime = createAsyncThunk(
  'securedApi/quietTimes/createQuietTime',
  async (quietTime, thunkAPI) => {
    try {
      const { quietTimeId: id } = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/quiet-time/`,
          { data: { ...quietTime } });
      return thunkAPI.fulfillWithValue({ ...quietTime, id });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

const deleteQuietTime = createAsyncThunk(
  'securedApi/quietTimes/deleteQuietTime',
  async (id, thunkAPI) => {
    try {
      await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/quiet-time/${id}`);
      return thunkAPI.fulfillWithValue(id);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

const initialState = {
  quietTimeList: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  error: false,
  loading: true,
  activeQuietTime: null,
};

export const quietTimesSlice = createSlice({
  name: 'quietTimes',
  initialState,
  reducers: {
    clearQuietTimesSlice: () => initialState,
    setActiveQuietTime: (state, action) => {
      state.activeQuietTime = action.payload;
    },
    removeQuietTime: (state, action) => {
      state.quietTimeList = [...state.quietTimeList]
        .filter((search) => search.id !== action.payload);
    },
    setQuietTimeCrudStatus: (state, action) => {
      state.crudStatus = action.payload;
    },
    setQuietTimeCrudOperation: (state, action) => {
      state.crudOperation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuietTime.pending, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchQuietTime.fulfilled, (state, action) => {
        const { quietTime } = action.payload;
        const foundIndex = state.quietTimeList
          .findIndex((range) => range.id === quietTime.id);
        if (foundIndex !== -1) {
          state.quietTimeList.splice(foundIndex, 1, quietTime);
        } else {
          state.quietTimeList.push(quietTime);
        }
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(fetchQuietTime.rejected, (state) => {
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(fetchQuietTimes.pending, (state) => {
        state.loading = true;
        state.crudOperation = CrudType.READ;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(fetchQuietTimes.fulfilled, (state, action) => {
        const { quietTimes } = action.payload;
        state.quietTimeList = quietTimes;
        state.crudStatus = CrudStatus.SUCCESS;
        state.loading = false;
        state.error = false;
      })
      .addCase(fetchQuietTimes.rejected, (state) => {
        state.error = true;
        state.loading = false;
        state.crudStatus = CrudStatus.ERROR;
      })
      .addCase(editQuietTime.pending, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(editQuietTime.fulfilled, (state, action) => {
        const newQuietTime = action.payload;
        const foundIndex = state.quietTimeList
          .findIndex((range) => range.id === newQuietTime.id);
        if (foundIndex !== -1) {
          state.quietTimeList.splice(foundIndex, 1, newQuietTime);
        } else {
          state.quietTimeList.push(newQuietTime);
        }
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(editQuietTime.rejected, (state) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(createQuietTime.pending, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(createQuietTime.fulfilled, (state, action) => {
        const newQuietTime = action.payload;
        const foundIndex = state.quietTimeList
          .findIndex((range) => range.id === newQuietTime.id);
        if (foundIndex !== -1) {
          state.quietTimeList.splice(foundIndex, 1, newQuietTime);
        } else {
          state.quietTimeList.push(newQuietTime);
        }
        state.activeQuietTime = newQuietTime;
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(createQuietTime.rejected, (state) => {
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      })
      .addCase(deleteQuietTime.pending, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
        state.error = false;
      })
      .addCase(deleteQuietTime.fulfilled, (state, action) => {
        state.quietTimeList = [...state.quietTimeList]
          .filter((search) => search.id !== action.payload);
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.SUCCESS;
        state.error = false;
      })
      .addCase(deleteQuietTime.rejected, (state) => {
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.ERROR;
        state.error = true;
      });
  },
});

const { actions, reducer } = quietTimesSlice;
export const {
  clearQuietTimesSlice,
  removeQuietTime,
  setActiveQuietTime,
  setQuietTimeCrudStatus,
  setQuietTimeCrudOperation,
} = actions;
export {
  selectQuietTimes,
  fetchQuietTimes,
  selectQuietTimeById,
  editQuietTime,
  createQuietTime,
  deleteQuietTime,
  selectQuietTimesCrudOperation,
  selectQuietTimesInitialLoad,
  selectQuietTimesError,
  selectQuietTimesCrudStatus,
  selectActiveQuietTime,
  fetchQuietTime,
};

export default reducer;
